import { COUNTRY_CODES, getCountryCodeByName } from '@dealstack/shared/lib/countries/countriesData';
import type {
	BankAccountFormatRuleRes,
	BankAccountIdentifierEntryDto,
} from '@dealstack/shared/lib/generated';
import type { BankAccountFormValues, BankAccountSubmitPayload } from './types.ts';

// Stored keys that mean the same thing under every format: an IBAN lookup's bic is the
// same bic a SWIFT tab asks for. Listed in the order the lookup summary shows them.
// Mirrors ruleSubmissionKeepers in bankAccount.repository — the unlinked rows a rule
// submission preserves are exactly the ones a rule tab may read.
export const FORMAT_INDEPENDENT_IDENTIFIER_KEYS: readonly string[] = [
	'bank_name',
	'account_number',
	'bic',
];

// A format that carries an `iban` field reuses the classic IBAN form wholesale
// (lookup, account number, BIC) instead of rule-driven inputs — the same experience
// as countries without any format rules. Its submission travels through the classic
// DTO fields, so no formatRuleId/identifiers are sent for it.
export const ruleUsesClassicIbanForm = (rule: BankAccountFormatRuleRes): boolean =>
	rule.fields.some((field) => field.key === 'iban');

// Address.country is free text (legacy names and ISO codes mixed). Only a value that
// resolves to a canonical code may preselect the bank country — never guess.
export const inferBankCountryCode = (addressCountry?: string | null): string | undefined => {
	const value = addressCountry?.trim();
	if (!value) {
		return undefined;
	}

	const upperCased = value.toUpperCase();
	if (COUNTRY_CODES.some((code) => code === upperCased)) {
		return upperCased;
	}

	return getCountryCodeByName(value) ?? undefined;
};

// The format-rule field values submit as generic identifier entries; without a chosen
// format the classic DTO fields carry everything and no entries are sent.
export const toIdentifierEntryDtos = (
	formValues: BankAccountFormValues
): BankAccountIdentifierEntryDto[] | undefined => {
	const record = formValues.identifiers;
	if (!formValues.formatRuleId || !record) {
		return undefined;
	}

	// option selects clear to null; the wire contract wants '' for a cleared value
	return Object.entries(record).map(([key, value]) => ({ key, value: value ?? '' }));
};

export const toBankAccountPayload = (
	formValues: BankAccountFormValues
): BankAccountSubmitPayload | null => {
	if (!formValues.currency) {
		return null;
	}

	// On the classic form the bank name is a field the user can see and clear, so '' has to
	// reach the server and clear the identifier — undefined leaves the previous bank's name
	// standing against a new IBAN. A rule tab renders no bank-name field: there it is
	// lookup-derived, and empty means "not looked up" rather than "cleared", so it stays
	// undefined and the stored row is left alone (the same rule bic follows).
	const bankName = formValues.formatRuleId
		? formValues.bankName || undefined
		: formValues.bankName ?? '';

	return {
		accountHolder: formValues.accountName,
		bankName,
		accountNumber: formValues.accountNumber,
		swiftCode: formValues.bic,
		iban: formValues.iban,
		// The looked-up country overwrites the picked one: a DE IBAN stores DE even
		// when GB was selected, so reopening the account shows DE and its formats.
		countryCode: formValues.verifiedCountryCode || formValues.countryCode || null,
		currency: formValues.currency,
		formatRuleId: formValues.formatRuleId || undefined,
		identifiers: toIdentifierEntryDtos(formValues),
	};
};

// The identifier->rule links pinpoint the format an account was entered under;
// the required-keys heuristic only remains for legacy rows without links.
export const inferFormatRule = (
	rules: BankAccountFormatRuleRes[],
	identifierValues: Record<string, string>,
	linkedRuleIds: ReadonlySet<string>
): BankAccountFormatRuleRes | undefined => {
	const linked = rules.find((rule) => linkedRuleIds.has(rule.id));
	if (linked) {
		return linked;
	}
	const presentKeys = new Set(
		Object.entries(identifierValues)
			.filter(([, value]) => !!value)
			.map(([key]) => key)
	);
	const match = rules.find((rule) =>
		rule.fields.filter((field) => field.required).every((field) => presentKeys.has(field.key))
	);
	return match ?? rules[0];
};
